import {Container} from "@mantine/core";
import {useNavigate, useSearchParams} from "react-router-dom";
import {
  DispatchRequest,
  getUseQueryResultData,
  transformDispatchRequest,
  useGetDispatchRequestQuery
} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {useMemo} from "react";
import {createSelector} from "@reduxjs/toolkit";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {ErrorPage} from "../../components/page/ErrorPage/ErrorPage";
import {LoadingPage} from "../../components/page/LoadingPage/LoadingPage";
import {DispatchRequestProfile} from "../../components/profile/DispatchRequestProfile/DispatchRequestProfile"
import {useTranslation} from "react-i18next";

type Props = {
  id?: string;
  showAcceptRejectActions?: boolean;
  showJobViewAction?: boolean;
}

type RouteProps = Omit<Props, "id">;

export const DispatchRequestProfilePageRoute = (props: RouteProps) => {
  const [params, _setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const id = params.get("id") ?? undefined;

  const {t} = useTranslation(["common"])

  if (!id) {
    navigate(-1);
    return (
      <ErrorPage
        message={t("common.entity.dispatchrequest", "Dispatch Request") + " " + t("common.error.notfound", "Not Found")}/>
    );
  }

  return <DispatchRequestProfilePage {...props} id={id}/>;
};

export const DispatchRequestProfilePage = (props: Props) => {
  const {id, showAcceptRejectActions, showJobViewAction} = props;

  const {t} = useTranslation(["common"])

  const selectDispatchRequest = useMemo(() => {
    return createSelector(
      [getUseQueryResultData<DispatchRequest>],
      (data) => {
        return data ? transformDispatchRequest(data) : undefined;
      }
    );
  }, []);

  const {dispatchRequest, isFetching} = useGetDispatchRequestQuery(id ?? skipToken, {
    selectFromResult: (result) => ({
      dispatchRequest: selectDispatchRequest(result),
      isFetching: result.isFetching
    })
  });

  if (isFetching) {
    return (
      <LoadingPage/>
    );
  }

  if (!dispatchRequest) {
    return (
      <ErrorPage
        message={t("common.entity.dispatchrequest", "Dispatch Request") + " " + t("common.error.notfound", "Not Found")}/>
    );
  }

  return (
    <ThemedPage>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <DispatchRequestProfile
            dispatchRequest={dispatchRequest}
            showAcceptRejectActions={showAcceptRejectActions}
            showJobViewAction={showJobViewAction}/>
        </Container>
      )}
    </ThemedPage>
  );
};
